/** Telehealth offerings and pricing from Open Arms Healthcare Services LLC brief */
export const telehealthServices = [
  {
    slug: 'sick-visits',
    title: 'Non-emergent sick visits',
    navLabel: 'Non-emergent sick visits',
    fee: '$25',
    feeDetail: 'per visit',
    summary:
      'Quick, effective care for common ailments such as colds, flu, coughs, UTIs, and STIs—without an in-person visit.',
    heading: 'Feel better, faster',
    description: [
      'When you\'re feeling unwell but don\'t require an emergency room visit, our virtual sick visits offer a convenient solution. We provide prompt evaluations and personalized treatment plans via secure video or phone consultations.',
    ],
    conditions: [
      'Colds, flu, and sinus infections',
      'Minor aches and pains',
      'Rashes and skin irritations',
      'Ear and throat infections',
    ],
    benefit: 'Get back on your feet faster with same-day virtual scheduling and affordable consultation fees—all without leaving your home.',
    keywords:
      'telehealth sick visit, virtual urgent care, cold flu telehealth, UTI telehealth, non-emergency care',
    heroImage: '/images/service-sick-visits.jpg',
    cardImage: '/images/service-sick-visits.jpg',
    detailImage: '/images/service-sick-detail.jpg',
    icon: 'stethoscope',
    layout: 'a',
    color: '#1565a8',
  },
  {
    slug: 'medication-refills',
    title: 'Medication refills',
    navLabel: 'Medication refills',
    fee: '$25',
    feeDetail: 'per visit',
    summary:
      'Easily obtain refills for your prescriptions so your treatment stays on track.',
    heading: 'Never miss a dose',
    description: [
      'Managing your medications should be stress-free and convenient. We provide quick and reliable prescription refill services through our telehealth platform.',
    ],
    conditions: [
      'Maintenance medications for chronic conditions',
      'Short-term prescriptions following a virtual sick visit',
      'Electronic coordination with your preferred pharmacy',
    ],
    benefit: 'We send prescriptions directly to your pharmacy with fast turnaround times, making refills easy and accessible from anywhere.',
    keywords: 'telehealth prescription refill, online medication refill, virtual refill visit',
    heroImage: '/images/service-medication-refills.jpg',
    cardImage: '/images/service-medication-refills.jpg',
    detailImage: '/images/service-medication-detail.jpg',
    icon: 'pill',
    layout: 'b',
    color: '#1565a8',
  },
  {
    slug: 'lab-orders',
    title: 'Lab order & interpretation',
    navLabel: 'Lab order & interpretation',
    fee: '$25',
    feeDetail: 'per service',
    summary:
      'We facilitate lab orders and provide clear, understandable interpretations of your results.',
    heading: 'Understand your results',
    description: [
      'Diagnostic testing is a vital part of understanding your health. We simplify the process with clear, easy-to-understand explanations of your results during virtual consultations.',
    ],
    conditions: [
      'Orders for necessary blood work and diagnostic tests at any lab of your choice',
      'Clear explanations of your results during virtual consultations',
      'Discussion of next steps and treatment plans based on your findings',
    ],
    benefit: 'Save time and avoid confusion with our streamlined virtual lab services. Our providers discuss your results directly with you online.',
    keywords: 'telehealth lab order, virtual lab results, lab interpretation telehealth',
    heroImage: '/images/service-lab-orders.jpg',
    cardImage: '/images/service-lab-orders.jpg',
    detailImage: '/images/service-lab-detail.jpg',
    icon: 'flask',
    layout: 'd',
    color: '#1565a8',
  },
  {
    slug: 'weight-loss',
    title: 'Weight loss management',
    navLabel: 'Weight loss management',
    fee: '$50',
    feeDetail: 'per month',
    summary:
      'Personalized guidance and support on your weight loss journey through virtual consultations.',
    heading: 'Your journey, your pace',
    description: [
      'Achieving a healthy weight is a personal journey, and our professional telehealth weight loss programs are designed to be sustainable and supportive. We meet you where you are and help you build habits that fit your life.',
    ],
    conditions: [
      'Lifestyle modification strategies',
      'Medical monitoring and support through virtual consultations',
    ],
    benefit: 'Reach your health goals in a warm, non-judgmental virtual environment with a plan that fits your unique lifestyle and budget, supported by our team every step of the way.',
    keywords: 'telehealth weight loss, virtual weight management, obesity support telehealth',
    heroImage: '/images/service-weight-loss.jpg',
    cardImage: '/images/service-weight-loss.jpg',
    detailImage: '/images/about-new2.jpg',
    icon: 'scale',
    layout: 'd',
    color: '#1565a8',
  },
  {
    slug: 'chronic-care',
    title: 'Chronic condition management & education',
    navLabel: 'Chronic condition management',
    fee: '$50',
    feeDetail: 'per visit',
    summary:
      'Expert support and education for conditions such as hypertension, diabetes, and high cholesterol.',
    heading: 'Consistent care, every step',
    description: [
      'Living with a chronic condition requires consistent, high-quality care, and our telehealth model makes it easier than ever. We partner with you through regular virtual check-ups, medication adjustments, and lifestyle guidance.',
    ],
    conditions: [
      'Hypertension (high blood pressure)',
      'Diabetes and blood sugar monitoring',
      'Asthma and COPD',
      'High cholesterol',
    ],
    benefit: 'Personalized virtual care plans and regular check-ins help you maintain your quality of life and prevent complications—continuous support from the comfort of your home.',
    keywords: 'telehealth diabetes, hypertension telehealth, chronic disease management virtual',
    heroImage: '/images/service-chronic-hero.jpg',
    cardImage: '/images/service-chronic-hero.jpg',
    detailImage: '/images/service-chronic-detail.jpg',
    icon: 'heartpulse',
    layout: 'a',
    color: '#1565a8',
  },
  {
    slug: 'smoking-cessation',
    title: 'Smoking cessation',
    navLabel: 'Smoking cessation',
    fee: '$25',
    feeDetail: 'per program (as listed)',
    summary:
      'Comprehensive virtual programs to help you quit smoking and improve your overall well-being.',
    heading: 'Take the first step',
    description: [
      'Choosing to quit smoking is one of the best decisions you can make for your health. Our telehealth program provides the tools and encouragement you need to succeed, right from wherever you are.',
    ],
    conditions: [
      'Medication management (if appropriate, with prescriptions sent to your pharmacy)',
      'Relapse prevention strategies and ongoing virtual encouragement',
    ],
    benefit: 'Increase your chances of quitting for good with a compassionate virtual team that celebrates every milestone with you.',
    keywords: 'telehealth quit smoking, virtual smoking cessation, nicotine cessation support',
    heroImage: '/images/service-smoking-cessation.jpg',
    cardImage: '/images/service-smoking-cessation.jpg',
    detailImage: '/images/service-smoking-detail.jpg',
    icon: 'cigaretteoff',
    layout: 'b',
    color: '#1565a8',
  },
];

export function getTelehealthServiceBySlug(slug) {
  return telehealthServices.find((s) => s.slug === slug) || null;
}

export function isValidServiceSlug(slug) {
  return telehealthServices.some((s) => s.slug === slug);
}
